import React from "react";
import { Badge, Button, Checkbox, HStack, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr, useColorModeValue } from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import RiskReviewStatusBadge from "../common/RiskReviewStatusBadge";
import { formatDate, getHighestRisk, getRequestId, getRequesterName } from "../../utils/riskReviewFormatters";

const riskColor = {
  CRITICAL: "red",
  HIGH: "orange",
  MEDIUM: "yellow",
  LOW: "green",
};

const getStageName = (approval, request) => {
  if (approval.stageName) return approval.stageName;
  const stageIndex = typeof request?.workflowStage === "number" ? request.workflowStage : null;
  const workflowStage = stageIndex !== null ? request?.workflowId?.stages?.[stageIndex] : null;
  return workflowStage?.stageName || `Level ${approval.level}`;
};

const RiskReviewApprovalTable = ({ approvals = [], selected = [], onSelect }) => {
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const headBg = useColorModeValue("gray.50", "gray.700");
  const hoverBg = useColorModeValue("gray.50", "gray.700");

  return (
    <TableContainer bg={cardBg} borderWidth="1px" borderColor={borderColor} borderRadius="md" boxShadow="sm">
      <Table size="sm">
        <Thead bg={headBg}>
          <Tr>
            <Th w="40px" py={3} />
            <Th>Request ID</Th>
            <Th>Title</Th>
            <Th>Risk</Th>
            <Th>Stage</Th>
            <Th>Status</Th>
            <Th>Requested By</Th>
            <Th>Created Date</Th>
            <Th textAlign="right">Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {approvals.map((approval) => {
            const request = approval.requestId;
            const requestId = request?._id || request;
            const highestRisk = getHighestRisk(request?.risks || []);
            const title =
              request?.projectName ||
              request?.sfdcOpportunityNo ||
              request?.requestType ||
              request?.category ||
              "Risk Review";
            return (
              <Tr key={approval._id} _hover={{ bg: hoverBg }}>
                <Td>
                  <Checkbox
                    isDisabled={approval.status !== "PENDING"}
                    isChecked={selected.includes(approval._id)}
                    onChange={(event) => onSelect?.(approval._id, event.target.checked)}
                  />
                </Td>
                <Td fontWeight="semibold" py={3}>
                  <Text>{typeof request === "object" ? getRequestId(request) : requestId}</Text>
                </Td>
                <Td maxW="300px">
                  <Text fontWeight="medium" noOfLines={1}>{title}</Text>
                  <Text color="gray.500" fontSize="xs" noOfLines={1}>
                    {[request?.businessUnit, request?.division, request?.country].filter(Boolean).join(" / ") || "—"}
                  </Text>
                </Td>
                <Td>
                  {highestRisk ? (
                    <Badge colorScheme={riskColor[highestRisk.severity] || "gray"}>{highestRisk.severity}</Badge>
                  ) : (
                    <Text color="gray.500">—</Text>
                  )}
                </Td>
                <Td>
                  <Text fontWeight="medium">{getStageName(approval, request)}</Text>
                  {approval.stageName ? (
                    <Text color="gray.500" fontSize="xs">
                      Level {approval.level}
                    </Text>
                  ) : null}
                </Td>
                <Td>
                  <RiskReviewStatusBadge value={request?.status || approval.status} />
                </Td>
                <Td>{typeof request === "object" ? getRequesterName(request) : "—"}</Td>
                <Td>{formatDate(request?.submittedAt || request?.createdAt || approval.assignedAt || approval.createdAt)}</Td>
                <Td textAlign="right">
                  <HStack justify="flex-end" spacing={2}>
                    {requestId ? (
                      <Button as={RouterLink} to={`/risk-review/requests/${requestId}`} size="sm" variant="outline" colorScheme="blue">
                        Review
                      </Button>
                    ) : null}
                  </HStack>
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

export default RiskReviewApprovalTable;
